import { createTheme } from "@mui/material/styles";

// AE brand palette (kept as a named export — consumed across the app).
export const AE_palette = {
  charcoal: '#2C2C2C',
  melonRed: '#FF455C',
  silver: '#E8E8E8',
  white: '#FFFFFF',
  seafoamGreen: '#45FFA7',
  seafoamGreen85: '#45FFA785',
  orchidPurple: '#C445FF',
  amber: '#FCC144',
  blue: '#58C4FF',
};

// OPUS-913 radius scale (--r-sm/md/lg). r-md is the default control radius.
const RADIUS = 10;
const R_SM = 7;
const R_LG = 14;
const SHADOW_LG = '0 18px 50px rgba(0,0,0,0.6)';
const CORAL = '#FF455C';           // brand/accent/focus/selected/error
const ELEVATED = '#2C2C2C';        // menus/dialogs/popovers (--elevated)
const BORDER = 'rgba(255,255,255,0.10)';
const BORDER_STRONG = 'rgba(255,255,255,0.18)';
const BORDER_SOFT = 'rgba(255,255,255,0.07)';
const PAPER = '#2A2E30';
const HEADER = '#31363A';
const onGreen = '#0B1F16'; // dark text for contrast on the light-green brand colour

/**
 * OPUS dashboard theme (OPUS-893 unification).
 *
 * Single source of truth for the design system: a complete typography scale,
 * full semantic colour roles, a border-radius/spacing baseline, and component
 * defaults (Button, Dialog, DataGrid, Chip, Card, inputs, …) so pages inherit a
 * consistent, finished look instead of ad-hoc sx values + hardcoded colours.
 */
const darkTheme = createTheme({
  palette: {
    mode: "dark",
    // Neutral primary (seafoam is reserved for accents/focus/selected, not CTAs).
    primary: { main: '#5C6266', contrastText: AE_palette.white },
    secondary: { main: '#3E4448', contrastText: AE_palette.white },
    success: { main: AE_palette.seafoamGreen, contrastText: onGreen },
    warning: { main: AE_palette.amber, contrastText: '#1A1300' },
    error: { main: AE_palette.melonRed, contrastText: AE_palette.white },
    info: { main: AE_palette.silver, contrastText: '#1A1A1A' },
    // Back-compat: existing color="seafoamGreen"/"seafoamGreen85" usages.
    seafoamGreen: { main: AE_palette.seafoamGreen, contrastText: onGreen },
    seafoamGreen85: { main: AE_palette.seafoamGreen85, contrastText: onGreen },
    // OPUS-913 named accents (coral=brand, blue/violet=status accents) so new
    // components can use color="coral"/"blue"/"violet".
    coral: { main: AE_palette.melonRed, contrastText: AE_palette.white },
    blue: { main: AE_palette.blue, contrastText: '#06222F' },
    violet: { main: AE_palette.orchidPurple, contrastText: '#1C0726' },
    background: { default: "#1A1A1A", paper: PAPER },
    text: {
      primary: AE_palette.white,
      secondary: 'rgba(255,255,255,0.62)',
      disabled: 'rgba(255,255,255,0.38)',
    },
    divider: BORDER,
    action: {
      hover: 'rgba(255,255,255,0.06)',
      selected: 'rgba(255,69,92,0.10)',
      disabled: 'rgba(255,255,255,0.30)',
      disabledBackground: 'rgba(255,255,255,0.10)',
    },
  },
  shape: { borderRadius: RADIUS },
  typography: {
    fontFamily: '"Barlow", sans-serif',
    fontSize: 16,
    // Display headings kept at dev's lighter weights (h1 600 / h2 500) — h2 is
    // the renderer-list name, which must read medium, not bold (OPUS-893).
    h1: { fontSize: '2rem', fontWeight: 600, lineHeight: 1.2 },
    h2: { fontSize: '1.75rem', fontWeight: 500, lineHeight: 1.25 },
    h3: { fontSize: '1.5rem', fontWeight: 500, lineHeight: 1.3 },
    h4: { fontSize: '1.375rem', fontWeight: 500, lineHeight: 1.3 },
    h5: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.35 },   // page titles
    h6: { fontSize: '1.0625rem', fontWeight: 600, lineHeight: 1.4 },  // section titles
    subtitle1: { fontSize: '1rem', fontWeight: 500, lineHeight: 1.5 },
    subtitle2: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.5 },
    body1: { fontSize: '1rem', fontWeight: 400, lineHeight: 1.55 },
    body2: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.5 },
    caption: { fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.4 },
    overline: { fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', lineHeight: 1.4 },
    button: { textTransform: 'none', fontWeight: 600, fontSize: '0.9375rem' },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        // Base font size knob (OPUS-893). Kept at dev parity (100% = 16px) so
        // the app is not oversized; the type scale below carries the sizing.
        html: { fontSize: '100%' },
        a: {
          color: CORAL,
          textDecoration: 'none',
          '&:hover': { textDecoration: 'underline' },
        },
        '*::-webkit-scrollbar': { width: 10, height: 10 },
        '*::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.085)', borderRadius: 99, border: '2px solid transparent', backgroundClip: 'padding-box' },
        '*::-webkit-scrollbar-thumb:hover': { background: 'rgba(255,255,255,0.13)', backgroundClip: 'padding-box' },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: RADIUS, textTransform: 'none', fontWeight: 600, paddingInline: 16 },
        sizeSmall: { paddingInline: 12 },
        outlined: { borderColor: 'rgba(255,255,255,0.24)' },
      },
    },
    MuiIconButton: { styleOverrides: { root: { borderRadius: R_SM } } },
    MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
    MuiCard: {
      styleOverrides: { root: { borderRadius: R_LG, border: `1px solid ${BORDER_SOFT}`, backgroundColor: PAPER } },
    },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: R_LG, border: `1px solid ${BORDER_STRONG}`, boxShadow: SHADOW_LG, backgroundColor: ELEVATED } },
    },
    MuiMenu: {
      styleOverrides: { paper: { borderRadius: RADIUS, border: `1px solid ${BORDER_STRONG}`, boxShadow: SHADOW_LG, backgroundColor: ELEVATED } },
    },
    MuiPopover: { styleOverrides: { paper: { boxShadow: SHADOW_LG } } },
    MuiAutocomplete: {
      styleOverrides: { paper: { borderRadius: RADIUS, border: `1px solid ${BORDER_STRONG}`, boxShadow: SHADOW_LG, backgroundColor: ELEVATED } },
    },
    MuiDialogTitle: { styleOverrides: { root: { fontSize: '1.125rem', fontWeight: 600, paddingTop: 20 } } },
    MuiDialogContent: { styleOverrides: { root: { paddingTop: 8 } } },
    MuiDialogActions: { styleOverrides: { root: { padding: 16, gap: 8 } } },
    MuiTextField: { defaultProps: { size: 'small', fullWidth: true } },
    MuiInputLabel: {
      styleOverrides: { root: { color: 'rgba(255,255,255,0.7)', '&.Mui-focused': { color: CORAL } } },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: RADIUS,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.18)' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.32)' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: CORAL },
        },
      },
    },
    MuiChip: { styleOverrides: { root: { borderRadius: 999, fontWeight: 600, fontSize: '0.7188rem' } } },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { fontSize: '0.75rem', backgroundColor: '#0F1112', border: `1px solid ${BORDER}` },
        arrow: { color: '#0F1112' },
      },
    },
    MuiDivider: { styleOverrides: { root: { borderColor: BORDER } } },
    MuiTableCell: { styleOverrides: { root: { borderColor: BORDER_SOFT } } },
    // OPUS-913: visible checkboxes (DataGrid select-all/row + forms) — the
    // default dark-on-dark unchecked box was barely visible on panel surfaces.
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: 'rgba(255,255,255,0.5)',
          '&.Mui-checked': { color: CORAL },
          '&.MuiCheckbox-indeterminate': { color: CORAL },
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        // OPUS-913: match the design DataTable across every grid in the app —
        // 13.5px cells, no vertical column separators, surface-2 row hover.
        root: { border: 'none', fontSize: '0.84375rem', '& .MuiDataGrid-columnSeparator': { display: 'none' } },
        row: {
          '&:hover': { backgroundColor: 'var(--surface-2)' },
          '&.Mui-selected': {
            backgroundColor: 'rgba(255,69,92,0.07)',
            boxShadow: 'inset 3px 0 0 #FF455C',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.045)' },
          },
        },
        columnHeaders: { backgroundColor: HEADER, borderRadius: 0, borderBottom: `1px solid ${BORDER}` },
        columnHeaderTitle: { fontWeight: 700, fontSize: '0.71875rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-dim)' },
        cell: {
          borderBottom: `1px solid ${BORDER}`,
          display: 'flex',
          alignItems: 'center',
          padding: '0 18px',
          color: 'var(--text)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          '&:focus': { outline: 'none' },
          '&:focus-within': { outline: 'none' },
        },
        columnHeader: { padding: '0 18px', '&:focus': { outline: 'none' }, '&:focus-within': { outline: 'none' } },
        footerContainer: { borderTop: `1px solid ${BORDER}` },
        overlay: { backgroundColor: 'transparent' },
      },
    },
  },
});

export default darkTheme;
